import { CHUNK_SIZE, JUMP_HEIGHT, PLAYER_HEIGHT_STANDING, PLAYER_HEIGHT_CROUCHING, PlayerState, TileType, PlayerInput, MAX_AIR_TICKS, UNDERGROUND_SPAWN_X, UNDERGROUND_SPAWN_Y, isQuicksand, isSandTile, SPAWN_CENTER_X, isKasiPlaceholder } from './constants';
import { WorldData, calculateSurfaceY, calculatePortalSurfaceY } from './WorldData';

// ============================================================
// PLAYER PHYSICS
// Shared deterministic physics for client prediction & server
// ============================================================

/**
 * Check if a tile type is passable (player can move through it)
 * SKY and PORTAL tiles are passable
 */
export function isPassable(tile: TileType): boolean {
    return tile === TileType.SKY || tile === TileType.PORTAL || tile === TileType.TRUNK || tile === TileType.SAPLING || tile === TileType.WATER_SURFACE || tile === TileType.BOOTS || tile === TileType.YELLOW_DOOR_OPEN || tile === TileType.YELLOW_KEY || tile === TileType.KASI_IJO || tile === TileType.KASI_UTALA || tile === TileType.KILI_LAWA || tile === TileType.KILI_SOWELI || isKasiPlaceholder(tile);
}

/**
 * Check if a tile is a ladder
 */
export function isLadder(tile: TileType): boolean {
    return tile === TileType.LADDER || tile === TileType.LEAVES || tile === TileType.STICK || tile === TileType.WATER || tile === TileType.KASI_LINJA;
}

/**
 * Check if a player's body can occupy this tile.
 * Used for horizontal movement body checks, head checks during jump/uncrouch,
 * and slash projectile travel. Ladder is body-passable (walk through, jump through)
 * but NOT ground-passable (you can stand on it).
 */
export function isBodyPassable(tile: TileType): boolean {
    return isPassable(tile) || isLadder(tile) || tile === TileType.KIWEN_SUPA;
}

export interface PlayerPhysicsState {
    x: number;
    y: number;
    state: PlayerState;
    facing: number;
    isCrouching: boolean;
    isHoldingBlock: boolean;
    heldBlockType: TileType | null;
    jumpRemaining: number;
    jumpsquatStartTime: number;
}

/**
 * Check if a player's head tile is underwater (WATER or WATER_SURFACE).
 * Player only breathes when their head is fully above water (not in any water tile).
 * At the surface, holding UP pokes the head above water so it's no longer in a water tile.
 */
export function isHeadInWater(playerX: number, playerY: number, getTile: (x: number, y: number) => TileType): boolean {
    const headTile = getTile(playerX, playerY);
    return headTile === TileType.WATER || headTile === TileType.WATER_SURFACE;
}

export function isOnGround(player: PlayerPhysicsState, world: WorldData): boolean {
    const playerHeight = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
    const feetY = player.y + playerHeight;
    const tileBelow = world.getTile(player.x, feetY);
    if (!isPassable(tileBelow)) return true;
    // Also grounded if body overlaps a ladder
    for (let y = 0; y < playerHeight; y++) {
        if (isLadder(world.getTile(player.x, player.y + y))) return true;
    }
    return false;
}

export function tryMoveHorizontal(
    player: PlayerPhysicsState,
    world: WorldData,
    dir: number
): boolean {
    const newX = player.x + dir;
    const playerHeight = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;

    // Check flat movement
    let canMoveFlat = true;
    for (let y = 0; y < playerHeight; y++) {
        const tile = world.getTile(newX, player.y + y);
        if (!isBodyPassable(tile)) {
            canMoveFlat = false;
            break;
        }
    }

    if (canMoveFlat) {
        player.x = newX;
        return true;
    }

    // Check step-up
    const feetLevel = player.y + playerHeight - 1;
    const frontBottomTile = world.getTile(newX, feetLevel);

    if (!isBodyPassable(frontBottomTile)) {
        // Quicksand prevents stepping up — player is stuck
        const tileUnderFeet = world.getTile(player.x, feetLevel + 1);
        if (isQuicksand(tileUnderFeet)) {
            return false;
        }

        let canStepUp = true;
        const newY = player.y - 1;

        for (let y = 0; y < playerHeight; y++) {
            const tile = world.getTile(newX, newY + y);
            if (!isBodyPassable(tile)) {
                canStepUp = false;
                break;
            }
        }

        if (canStepUp) {
            player.x = newX;
            player.y = newY;
            return true;
        }
    }

    return false;
}

export function tryMoveUp(player: PlayerPhysicsState, world: WorldData): boolean {
    const newY = player.y - 1;
    const tile = world.getTile(player.x, newY);

    if (!isBodyPassable(tile)) {
        return false;
    }

    player.y = newY;
    return true;
}

export function tryMoveDown(player: PlayerPhysicsState, world: WorldData): boolean {
    const playerHeight = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
    const newFeetY = player.y + playerHeight;

    const tile = world.getTile(player.x, newFeetY);
    if (!isPassable(tile)) {
        return false;
    }

    player.y++;
    return true;
}

export function startCrouch(player: PlayerPhysicsState): void {
    if (player.isCrouching) return;
    player.y += 1;
    player.isCrouching = true;
}

export function endCrouch(player: PlayerPhysicsState, world: WorldData): boolean {
    if (!player.isCrouching) return false;

    const aboveTile = world.getTile(player.x, player.y - 1);
    if (isBodyPassable(aboveTile)) {
        player.y -= 1;
        player.isCrouching = false;
        return true;
    }
    return false;
}

export function startJump(player: PlayerPhysicsState, world: WorldData, currentTime: number): void {
    if (player.isCrouching) {
        if (!endCrouch(player, world)) {
            return;
        }
    }
    player.state = PlayerState.JUMPSQUAT;
    player.jumpsquatStartTime = currentTime;
    player.jumpRemaining = JUMP_HEIGHT;
}

// Result of a block action attempt
export interface BlockActionResult {
    success: boolean;
    action?: 'pickup' | 'place' | 'combine';
    blockX?: number;
    blockY?: number;
    blockType?: TileType;
}

export function handleDownAction(
    player: PlayerPhysicsState,
    world: WorldData
): BlockActionResult {
    const frontX = player.x + player.facing;
    const playerHeight = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
    const feetY = player.y + playerHeight - 1;
    const frontBottomY = feetY;
    const frontTopY = feetY - 1;

    const frontBottomTile = world.getTile(frontX, frontBottomY);
    const frontTopTile = world.getTile(frontX, frontTopY);

    if (player.isHoldingBlock) {
        // STICK-on-STICK combination: delete the front stick, convert held to LADDER
        if (player.heldBlockType === TileType.STICK) {
            if (frontBottomTile === TileType.STICK) {
                world.setTile(frontX, frontBottomY, TileType.SKY);
                world.removeFallingBlock(frontX, frontBottomY);
                player.heldBlockType = TileType.LADDER;
                return { success: true, action: 'combine', blockX: frontX, blockY: frontBottomY, blockType: TileType.STICK };
            } else if (frontTopTile === TileType.STICK) {
                world.setTile(frontX, frontTopY, TileType.SKY);
                world.removeFallingBlock(frontX, frontTopY);
                player.heldBlockType = TileType.LADDER;
                return { success: true, action: 'combine', blockX: frontX, blockY: frontTopY, blockType: TileType.STICK };
            }
        }

        // PLANK gap-bridging: when both front tiles are SKY, player is on solid ground,
        // and the tile diagonally in front+below is also SKY, place the plank 1 tile lower
        if (player.heldBlockType === TileType.PLANK
            && frontBottomTile === TileType.SKY && frontTopTile === TileType.SKY
            && !isPassable(world.getTile(player.x, feetY + 1))
            && world.getTile(frontX, feetY + 1) === TileType.SKY) {
            const placeY = feetY + 1;
            world.setTile(frontX, placeY, TileType.PLANK);
            player.isHoldingBlock = false;
            player.heldBlockType = null;
            return { success: true, action: 'place', blockX: frontX, blockY: placeY, blockType: TileType.PLANK };
        }

        // Try to place block
        // Note: Can only place on SKY tiles, not PORTAL (portal door frame is protected)
        // Exception: LADDER can be placed below an existing LADDER (extending downward)
        if (frontBottomTile === TileType.SKY && (frontTopTile === TileType.SKY || (player.heldBlockType === TileType.LADDER && frontTopTile === TileType.LADDER))) {
            world.setTile(frontX, frontBottomY, player.heldBlockType!);
            if (player.heldBlockType === TileType.DIRT || player.heldBlockType === TileType.GRASS || player.heldBlockType === TileType.SKULL || player.heldBlockType === TileType.STICK || player.heldBlockType === TileType.LOG || player.heldBlockType === TileType.FLINT || player.heldBlockType === TileType.WOODEN_SHIELD || player.heldBlockType === TileType.STONE_CLUB || player.heldBlockType === TileType.IRON_SWORD
                || player.heldBlockType === TileType.KILI_AKESI || player.heldBlockType === TileType.KILI_ALE || player.heldBlockType === TileType.KILI_IKE || player.heldBlockType === TileType.KILI_JELO || player.heldBlockType === TileType.KILI_KEN || player.heldBlockType === TileType.KILI_KIWEN || player.heldBlockType === TileType.KILI_KULUPU || player.heldBlockType === TileType.KILI_LAWA || player.heldBlockType === TileType.KILI_SOWELI
                || isSandTile(player.heldBlockType!)) {
                world.addFallingBlock(frontX, frontBottomY);
            }
            const blockType = player.heldBlockType!;
            player.isHoldingBlock = false;
            player.heldBlockType = null;
            return { success: true, action: 'place', blockX: frontX, blockY: frontBottomY, blockType };
        } else if (frontTopTile === TileType.SKY && frontBottomTile !== TileType.SKY && !isPassable(frontBottomTile)) {
            // Can place on top of solid block, but not on PORTAL
            world.setTile(frontX, frontTopY, player.heldBlockType!);
            if (player.heldBlockType === TileType.DIRT || player.heldBlockType === TileType.GRASS || player.heldBlockType === TileType.SKULL || player.heldBlockType === TileType.STICK || player.heldBlockType === TileType.LOG || player.heldBlockType === TileType.FLINT || player.heldBlockType === TileType.WOODEN_SHIELD || player.heldBlockType === TileType.STONE_CLUB || player.heldBlockType === TileType.IRON_SWORD
                || player.heldBlockType === TileType.KILI_AKESI || player.heldBlockType === TileType.KILI_ALE || player.heldBlockType === TileType.KILI_IKE || player.heldBlockType === TileType.KILI_JELO || player.heldBlockType === TileType.KILI_KEN || player.heldBlockType === TileType.KILI_KIWEN || player.heldBlockType === TileType.KILI_KULUPU || player.heldBlockType === TileType.KILI_LAWA || player.heldBlockType === TileType.KILI_SOWELI
                || isSandTile(player.heldBlockType!)) {
                world.addFallingBlock(frontX, frontTopY);
            }
            const blockType = player.heldBlockType!;
            player.isHoldingBlock = false;
            player.heldBlockType = null;
            return { success: true, action: 'place', blockX: frontX, blockY: frontTopY, blockType };
        } else if (player.heldBlockType === TileType.LADDER && frontBottomTile === TileType.LADDER && frontTopTile === TileType.LADDER) {
            // Ladder extension: both front tiles are ladder, seek up to place at top
            let scanY = frontTopY - 1;
            while (world.getTile(frontX, scanY) === TileType.LADDER) {
                scanY--;
            }
            if (world.getTile(frontX, scanY) === TileType.SKY) {
                world.setTile(frontX, scanY, TileType.LADDER);
                player.isHoldingBlock = false;
                player.heldBlockType = null;
                return { success: true, action: 'place', blockX: frontX, blockY: scanY, blockType: TileType.LADDER };
            }
        }
    } else {
        // Try to pick up block
        if (frontBottomTile === TileType.DIRT && isBodyPassable(frontTopTile)) {
            world.setTile(frontX, frontBottomY, TileType.SKY);
            player.isHoldingBlock = true;
            player.heldBlockType = TileType.DIRT;
            return { success: true, action: 'pickup', blockX: frontX, blockY: frontBottomY, blockType: TileType.DIRT };
        }
        // Sticks can be picked up even under other blocks
        if (frontBottomTile === TileType.STICK) {
            world.setTile(frontX, frontBottomY, TileType.SKY);
            player.isHoldingBlock = true;
            player.heldBlockType = TileType.STICK;
            return { success: true, action: 'pickup', blockX: frontX, blockY: frontBottomY, blockType: TileType.STICK };
        }
        // Planks can be picked up (solid, non-gravity block)
        if (frontBottomTile === TileType.PLANK && isBodyPassable(frontTopTile)) {
            world.setTile(frontX, frontBottomY, TileType.SKY);
            player.isHoldingBlock = true;
            player.heldBlockType = TileType.PLANK;
            return { success: true, action: 'pickup', blockX: frontX, blockY: frontBottomY, blockType: TileType.PLANK };
        }
    }

    return { success: false };
}

/**
 * Find position where player stands directly on surface (no falling).
 * Player stands on top of the surface block.
 * Skips passable tiles (SKY, PORTAL) to find actual ground.
 */
export function findStandingPosition(world: WorldData, startX: number = SPAWN_CENTER_X): { x: number; y: number } {
    // Scan down to find first solid block (not passable)
    for (let y = -20; y < 30; y++) {
        const tile = world.getTile(startX, y);
        if (!isPassable(tile)) {
            // Found surface at y, player stands above it at y-3
            return { x: startX, y: y - 3 };
        }
    }
    // Fallback
    return { x: startX, y: -5 };
}

/**
 * Find spawn position at the portal door (at SPAWN_CENTER_X+1).
 * Uses terrain formula to calculate exact position, ignoring any blocks above.
 * Player spawns standing in the portal door frame, on top of the rune.
 */
export function findPortalSpawnPosition(world: WorldData): { x: number; y: number } {
    const surfaceY = calculatePortalSurfaceY(world.seed);

    // Rune is at SPAWN_CENTER_X+1, surfaceY-7 (roof level), player stands on top of it
    // Player occupies y and y+1, feet at y+1 standing on the rune
    // So player.y = (surfaceY-7) - 2 means feet at surfaceY-7-1+1 = surfaceY-7, standing on rune
    return { x: SPAWN_CENTER_X + 1, y: surfaceY - 9 };
}

/**
 * Default spawn position in the underground cavern.
 */
export function findDefaultSpawnPosition(): { x: number; y: number } {
    return { x: UNDERGROUND_SPAWN_X, y: UNDERGROUND_SPAWN_Y };
}
